import * as _ from 'lodash';
import { DateTime } from 'luxon';

import type { AccessibleOU } from '@/models/FuelUserAccess';
import type { OU } from '@/models/Ou';
import type { AppAnalyticsState } from '@/pages/AppAnalytics/AppAnalytics';

import { dateUtils } from './dateUtils';

export type GroupItem = {
  id: string;
  name: string;
  alias?: string;
};

export type RangeType =
  | 'Last 24 Hours'
  | 'Last 48 Hours'
  | 'Today'
  | 'Yesterday'
  | 'Last 7 Days'
  | 'Last 30 Days'
  | 'This Month'
  | 'Last Month'
  | 'Last 3 Months'
  | 'Last 6 Months'
  | 'Last 12 Months'
  | 'Custom';

export interface TimeScaleData {
  offset: number;
  fromDate: string | null;
  toDate: string | null;
  timePoints: string[];
  timePart: 'HOUR' | 'DAY' | 'MONTH' | null;
  timeFormat: string | null;
}

const analyticsUtils = {
  formatWatchTime(value: string | number) {
    let totalSeconds = +value;
    if (totalSeconds == null) {
      return '-';
    }
    const isNegative = totalSeconds < 0;
    totalSeconds = Math.abs(totalSeconds);
    if (totalSeconds > 0) {
      totalSeconds = Math.round(totalSeconds);
      const secondsInWeek = 7 * 24 * 3600; // 604800
      const secondsInDay = 24 * 3600; // 86400
      const secondsInHour = 3600;
      const secondsInMinute = 60;

      let result = '';

      // If >= 1 week, show weeks and days (rounded to nearest)
      if (totalSeconds >= secondsInWeek) {
        const roundedHours = Math.round(totalSeconds / secondsInHour);
        const weeks = Math.floor(roundedHours / (24 * 7));
        const remainingDays = Math.round((roundedHours % (24 * 7)) / 24);
        if (remainingDays > 0) {
          result = `${weeks}w ${remainingDays}d`;
        } else {
          result = `${weeks}w`;
        }
      } else if (totalSeconds >= secondsInDay) {
        // If >= 1 day, show days and hours (rounded to nearest)
        const roundedHours = Math.round(totalSeconds / secondsInHour);
        const days = Math.floor(roundedHours / 24);
        const remainingHours = roundedHours % 24;
        if (remainingHours > 0) {
          result = `${days}d ${remainingHours}h`;
        } else {
          result = `${days}d`;
        }
      } else if (totalSeconds >= secondsInHour) {
        // If >= 1 hour, show hours and minutes (rounded to nearest)
        const roundedMinutes = Math.round(totalSeconds / secondsInMinute);
        const hours = Math.floor(roundedMinutes / 60);
        const remainingMinutes = roundedMinutes % 60;
        if (remainingMinutes > 0) {
          result = `${hours}h ${remainingMinutes}m`;
        } else {
          result = `${hours}h`;
        }
      } else if (totalSeconds >= secondsInMinute) {
        // If >= 1 minute, show minutes and seconds
        const minutes = Math.floor(totalSeconds / secondsInMinute);
        const remainingSeconds = totalSeconds % secondsInMinute;
        if (remainingSeconds > 0) {
          result = `${minutes}m ${remainingSeconds}s`;
        } else {
          result = `${minutes}m`;
        }
      } else {
        // Otherwise show seconds
        result = `${totalSeconds}s`;
      }

      return isNegative ? `-${result}` : result;
    }
    return '0s';
  },
  formatWatchTimeInHours(value: string | number) {
    const totalSeconds = +value;
    if (totalSeconds == null || Number.isNaN(totalSeconds)) {
      return '0h';
    }
    const hours = totalSeconds / 3600;
    const formatted = hours.toFixed(2);
    // Remove trailing zeros (e.g., "2.50" -> "2.5", "2.00" -> "2")
    const hoursStr = parseFloat(formatted).toString();
    return `${hoursStr}h`;
  },
  formatCount(value: string | number): string {
    let count = +value;
    if (count == null || Number.isNaN(count)) {
      return '0';
    }
    const isNegative = count < 0;
    count = Math.abs(count);

    if (count === 0) {
      return '0';
    }

    const sextillion = 1000000000000000000000; // 10^21
    const quintillion = 1000000000000000000; // 10^18
    const quadrillion = 1000000000000000; // 10^15
    const trillion = 1000000000000; // 10^12
    const billion = 1000000000; // 10^9
    const million = 1000000; // 10^6
    const thousand = 1000; // 10^3

    let result = '';

    // If >= 1 Sextillion, format as Sx
    if (count >= sextillion) {
      const rounded = Math.round((count / sextillion) * 100) / 100; // Round to 2 decimals
      const formatted = rounded.toFixed(2);
      // Remove trailing zeros (e.g., "2.00" -> "2", "2.50" -> "2.5")
      const roundedStr = parseFloat(formatted).toString();
      result = `${roundedStr}Sx`;
    } else if (count >= quintillion) {
      // If >= 1 Quintillion, format as Qi
      const rounded = Math.round((count / quintillion) * 100) / 100; // Round to 2 decimals
      const formatted = rounded.toFixed(2);
      // Remove trailing zeros (e.g., "2.00" -> "2", "2.50" -> "2.5")
      const roundedStr = parseFloat(formatted).toString();
      result = `${roundedStr}Qi`;
    } else if (count >= quadrillion) {
      // If >= 1 Quadrillion, format as Q
      const rounded = Math.round((count / quadrillion) * 100) / 100; // Round to 2 decimals
      const formatted = rounded.toFixed(2);
      // Remove trailing zeros (e.g., "2.00" -> "2", "2.50" -> "2.5")
      const roundedStr = parseFloat(formatted).toString();
      result = `${roundedStr}Q`;
    } else if (count >= trillion) {
      // If >= 1 Trillion, format as T
      const rounded = Math.round((count / trillion) * 100) / 100; // Round to 2 decimals
      const formatted = rounded.toFixed(2);
      // Remove trailing zeros (e.g., "2.00" -> "2", "2.50" -> "2.5")
      const roundedStr = parseFloat(formatted).toString();
      result = `${roundedStr}T`;
    } else if (count >= billion) {
      // If >= 1 Billion, format as B
      const rounded = Math.round((count / billion) * 100) / 100; // Round to 2 decimals
      const formatted = rounded.toFixed(2);
      // Remove trailing zeros (e.g., "2.00" -> "2", "2.50" -> "2.5")
      const roundedStr = parseFloat(formatted).toString();
      result = `${roundedStr}B`;
    } else if (count >= million) {
      // If >= 1 Million, format as M
      const rounded = Math.round((count / million) * 100) / 100; // Round to 2 decimals
      const formatted = rounded.toFixed(2);
      // Remove trailing zeros (e.g., "2.00" -> "2", "2.50" -> "2.5")
      const roundedStr = parseFloat(formatted).toString();
      result = `${roundedStr}M`;
    } else if (count >= thousand) {
      // If >= 1 Thousand, format as K
      const rounded = Math.round((count / thousand) * 100) / 100; // Round to 2 decimals
      const formatted = rounded.toFixed(2);
      // Remove trailing zeros (e.g., "2.00" -> "2", "2.50" -> "2.5")
      const roundedStr = parseFloat(formatted).toString();
      result = `${roundedStr}K`;
    } else {
      // Less than 1000, return as-is
      result = Math.round(count).toString();
    }

    return isNegative ? `-${result}` : result;
  },
  formatBytes(value: string | number) {
    let totalBytes = +value;
    if (totalBytes == null) {
      return '-';
    }
    const isNegative = totalBytes < 0;
    totalBytes = Math.abs(totalBytes);
    if (totalBytes > 0) {
      totalBytes = Math.round(totalBytes);
      const bytesInPB = 1024 ** 5; // 1,125,899,906,842,624
      const bytesInTB = 1024 ** 4; // 1,099,511,627,776
      const bytesInGB = 1024 ** 3; // 1,073,741,824
      const bytesInMB = 1024 ** 2; // 1,048,576
      const bytesInKB = 1024; // 1024

      let result = '';

      // If >= 1 PB, round to nearest PB and show only PB
      if (totalBytes >= bytesInPB) {
        const roundedPB = (totalBytes / bytesInPB).toFixed(1);
        result = `${roundedPB} PB`;
      } else if (totalBytes >= bytesInTB) {
        // If >= 1 TB, round to nearest TB and show only TB
        const roundedTB = (totalBytes / bytesInTB).toFixed(1);
        result = `${roundedTB} TB`;
      } else if (totalBytes >= bytesInGB) {
        // If >= 1 GB, round to nearest GB and show only GB
        const roundedGB = (totalBytes / bytesInGB).toFixed(1);
        result = `${roundedGB} GB`;
      } else if (totalBytes >= bytesInMB) {
        // If >= 1 MB, round to nearest MB and show only MB
        const roundedMB = (totalBytes / bytesInMB).toFixed(1);
        result = `${roundedMB} MB`;
      } else if (totalBytes >= bytesInKB) {
        // If >= 1 KB, round to nearest KB and show only KB
        const roundedKB = (totalBytes / bytesInKB).toFixed(1);
        result = `${roundedKB} KB`;
      } else {
        // Otherwise show bytes
        result = `${totalBytes} B`;
      }

      return isNegative ? `-${result}` : result;
    }
    return '0 B';
  },
  getCountryName(countryCode: string, locale = 'en') {
    try {
      const regionNames = new Intl.DisplayNames([locale], { type: 'region' });
      return regionNames.of(countryCode);
    } catch (error) {
      console.error('Error getting country name:', error);
      return null; // Or handle the error as appropriate
    }
  },

  cleanseOUIds(ouIds: string[], accessibleOUs: AccessibleOU[]) {
    if (ouIds == null) {
      return null;
    }
    let toReturn: string[] = [];
    if (ouIds?.length > 0) {
      toReturn = ouIds.filter((x) => accessibleOUs.find((y) => y.ouId === x));
      toReturn = _.uniq(toReturn);
    }
    return toReturn;
  },

  correctDates(data: Array<{ eventdate?: string }>, offsetMinutes: number) {
    for (let i = 0; i < data.length; i++) {
      if (data[i].eventdate) {
        data[i].eventdate = DateTime.fromISO(`${data[i].eventdate}.000Z`)
          .minus({ minutes: offsetMinutes }) // Reverse the offset
          .toUTC()
          .toISO();
      }
    }
    return data;
  },

  formatTimePointsForChart(timePoints: string[], timeFormat: string | null = null): string[] {
    // console.log('formatTimePointsForChart', { timePoints, timeFormat });
    const format = timeFormat || 'YYYY/MM';
    return timePoints.map((tp) => {
      if (format === 'YYYY/MM') {
        const d = new Date(tp);
        const month = d.toLocaleString('default', { month: 'short' });
        const year = String(d.getFullYear()).slice(-2);
        return `${month} '${year}`;
      }
      if (format === 'MM/dd' || format === 'YYYY/MM/DD') {
        const d = new Date(tp);
        const month = d.toLocaleString('default', { month: 'short' });
        const day = d.getDate();
        return `${day} ${month}`;
        // return d.toLocaleDateString('default', { month: 'short', day: 'numeric' });
      }
      // For HOUR format or others, use a more compact format
      const d = new Date(tp);
      return d.toLocaleString('default', { month: 'short', day: 'numeric', hour: 'numeric' });
    });
  },

  getChartOptions(
    title: string,
    legendLabels: string[],
    xLabels: string[],
    series: any[],
    valueFormatter: (value: string) => string | null = null,
    swapOrientation: boolean = false
  ) {
    // console.log('getChartOptions', { title, legendLabels, xLabels, series, swapOrientation });
    // Get the totals
    const chartOptions = {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          color: '#2c3e50',
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        textStyle: { color: '#333' },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        axisLine: { lineStyle: { color: '#ddd' } },
        axisLabel: { color: '#666' },
      },
      yAxis: {
        type: 'value',
        smooth: true,
        axisLine: { lineStyle: { color: '#ddd' } },
        axisLabel: { color: '#666' },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series,
    };

    if (legendLabels?.length > 0) {
      chartOptions.legend = {
        data: legendLabels,
        top: 30,
        textStyle: { color: '#666' },
      };
    }

    if (swapOrientation === true) {
      chartOptions.xAxis.type = 'value';
      chartOptions.yAxis.type = 'category';
      chartOptions.yAxis.data = xLabels;
    } else {
      chartOptions.xAxis.data = xLabels;
    }

    if (valueFormatter) {
      if (swapOrientation === true) {
        chartOptions.xAxis.axisLabel.formatter = valueFormatter;
      } else {
        chartOptions.yAxis.axisLabel.formatter = valueFormatter;
      }
      chartOptions.tooltip = {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        textStyle: { color: '#333' },
        formatter(params) {
          let html = `<div>${params[0].name}</div>`;
          params.forEach(function (item) {
            // Format the value to two decimal places
            html += `<div>${item.marker} ${item.seriesName
              }&nbsp;<span style="font-weight: bold; float: right; margin-left: 20px">${valueFormatter(item.value)}</span></div>`;
          });
          return html;
        },
      };
    }

    return chartOptions;
  },
  getCountryChartOptions(
    title: string,
    countryCodes: string[],
    series: unknown[],
    data: unknown[],
    formatter: (val: string) => string | null = null
  ) {
    const chartOptions = {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          color: '#2c3e50',
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        // formatter(params) {
        //   return `${ params.seriesName } <br>
        //   ${params.marker}${params.name}<span style="float: right; margin-left: 20px"><b>${params.value}</b> (${params.percent}%)</span>`;
        // },
        formatter(params) {
          let html = `<div>${params[0].name}</div>`;
          let total = 0;
          params.forEach(function (item) {
            total += item.value;
            // Format the value to two decimal places
            html += `<div>${item.marker} ${item.seriesName}<span style="font-weight: bold; float: right; margin-left: 20px">${formatter ? formatter(item.value) : item.value
              }</span></div>`;
          });
          html += `<div>Total<span style="font-weight: bold; float: right; margin-left: 20px">${formatter ? formatter(total) : total
            }</span></div>`;
          return html;
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e0e0e0',
        borderWidth: 1,
        textStyle: { color: '#333' },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        // name: axisLabel,
        axisLine: { lineStyle: { color: '#ddd' } },
        axisLabel: { color: '#666' },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      yAxis: {
        type: 'category',
        data: countryCodes,
        axisLine: { lineStyle: { color: '#ddd' } },
        axisLabel: { color: '#666' },
      },
      series,
    };

    if (formatter != null) {
      chartOptions.xAxis.axisLabel.formatter = formatter;
    }

    return chartOptions;
  },
  getLegendsByGroup(params: AppAnalyticsState) {
    return (params.groupByList || []).map((g) => g.name);
  },
  getTimeLabels(params: AppAnalyticsState, ouData: OU) {
    return params.timePoints.map((x) => dateUtils.format(x, ouData.timezone, params.timeFormat));
  },
  matchesGroup(params: AppAnalyticsState, value: string, g: GroupItem) {
    if (params.groupBy === 'channel') {
      return value === g.id || (!!g.alias && value === g.alias);
    }
    return value === g.id;
  },
};

// Returns in UTC the x-axis timescale points
export const getTimeScaleData = (
  rangeType: RangeType,
  timezone: string,
  customFromDate?: string,
  customToDate?: string
): TimeScaleData => {
  let offset: number = 0;
  const timePoints: DateTime[] = [];
  let fromDate: DateTime | null = null;
  let toDate: DateTime | null = null;
  let timeFormat: string | null = null;
  let timePart: 'HOUR' | 'DAY' | 'MONTH' | null = null;

  const now = dateUtils.now();
  let refFromDate: DateTime | null = null;
  let refToDate: DateTime | null = null;
  let ref: DateTime | null = null;

  switch (rangeType) {
    case 'Last 24 Hours':
      // Build an array of the last 24 hours
      timePart = 'HOUR';
      fromDate = now.setZone(timezone).minus({ hour: 23 }).startOf('hour').toUTC();
      toDate = now.setZone(timezone).endOf('hour').toUTC();
      ref = fromDate;
      while (ref && ref <= toDate) {
        timePoints.push(ref);
        ref = ref.plus({ hour: 1 });
      }
      break;
    case 'Last 48 Hours':
      // Build an array of the last 48 hours
      timePart = 'HOUR';
      fromDate = now.setZone(timezone).minus({ hour: 47 }).startOf('hour').toUTC();
      toDate = now.setZone(timezone).endOf('hour').toUTC();
      ref = fromDate;
      while (ref && ref <= toDate) {
        timePoints.push(ref);
        ref = ref.plus({ hour: 1 });
      }
      break;
    case 'Today':
      timePart = 'HOUR';
      fromDate = now.setZone(timezone).startOf('day').toUTC();
      toDate = now.setZone(timezone).endOf('day').toUTC();
      ref = fromDate;
      while (ref && ref <= toDate) {
        timePoints.push(ref);
        ref = ref.plus({ hour: 1 });
      }
      break;
    case 'Yesterday':
      timePart = 'HOUR';
      fromDate = now.setZone(timezone).minus({ day: 1 }).startOf('day').toUTC();
      toDate = now.setZone(timezone).minus({ day: 1 }).endOf('day').toUTC();
      ref = fromDate;
      while (ref && ref <= toDate) {
        timePoints.push(ref);
        ref = ref.plus({ hour: 1 });
      }
      break;
    case 'Last 7 Days':
      timePart = 'DAY';
      fromDate = now.setZone(timezone).minus({ day: 7 }).startOf('day').toUTC();
      toDate = now.setZone(timezone).endOf('day').toUTC();
      offset = dateUtils.getTimezoneOffsetMinutes(timezone);
      refFromDate = now.toUTC().minus({ day: 7 }).startOf('day').minus({ minutes: offset });
      refToDate = now.toUTC().endOf('day').minus({ minutes: offset });
      ref = refFromDate;
      while (ref && ref <= refToDate) {
        timePoints.push(ref); // The SQL will return 00 for the hours when grouping by day
        ref = ref.plus({ day: 1 });
      }
      break;
    case 'Last 30 Days':
      timePart = 'DAY';
      fromDate = now.setZone(timezone).minus({ day: 29 }).startOf('day').toUTC();
      toDate = now.setZone(timezone).endOf('day').toUTC();
      offset = dateUtils.getTimezoneOffsetMinutes(timezone);
      refFromDate = now.toUTC().startOf('day').minus({ day: 30 }).minus({ minutes: offset });
      refToDate = now.toUTC().endOf('day').minus({ minutes: offset });
      ref = refFromDate;
      while (ref && ref <= refToDate) {
        timePoints.push(ref); // The SQL will return 00 for the hours when grouping by day
        ref = ref.plus({ day: 1 });
      }
      break;
    case 'This Month':
      timePart = 'DAY';
      fromDate = now.setZone(timezone).startOf('month').toUTC();
      toDate = now.setZone(timezone).endOf('month').toUTC();
      offset = dateUtils.getTimezoneOffsetMinutes(timezone);
      refFromDate = now.toUTC().startOf('month').minus({ minutes: offset });
      refToDate = now.toUTC().endOf('month').minus({ minutes: offset });
      ref = refFromDate;
      while (ref && ref <= refToDate) {
        timePoints.push(ref); // The SQL will return 00 for the hours when grouping by day
        ref = ref.plus({ day: 1 });
      }
      break;
    case 'Last Month':
      timePart = 'DAY';
      fromDate = now.setZone(timezone).minus({ month: 1 }).startOf('month').toUTC();
      toDate = now.setZone(timezone).minus({ month: 1 }).endOf('month').toUTC();
      offset = dateUtils.getTimezoneOffsetMinutes(timezone);
      refFromDate = now.toUTC().startOf('month').minus({ month: 1 }).minus({ minutes: offset });
      refToDate = now.toUTC().endOf('month').minus({ month: 1 }).minus({ minutes: offset });
      ref = refFromDate;
      while (ref && ref <= refToDate) {
        timePoints.push(ref); // The SQL will return 00 for the hours when grouping by day
        ref = ref.plus({ day: 1 });
      }
      break;
    case 'Last 3 Months':
      timePart = 'MONTH';
      fromDate = now.setZone(timezone).minus({ month: 2 }).startOf('month').toUTC();
      toDate = now.setZone(timezone).endOf('month').toUTC();
      offset = dateUtils.getTimezoneOffsetMinutes(timezone);
      refFromDate = now.toUTC().startOf('month').minus({ month: 2 }).minus({ minutes: offset });
      refToDate = now.toUTC().endOf('month').minus({ minutes: offset });
      ref = refFromDate;
      while (ref && ref <= refToDate) {
        timePoints.push(ref);
        ref = ref.plus({ month: 1 });
      }
      break;
    case 'Last 6 Months':
      timePart = 'MONTH';
      fromDate = now.setZone(timezone).minus({ month: 5 }).startOf('month').toUTC();
      toDate = now.setZone(timezone).endOf('month').toUTC();
      offset = dateUtils.getTimezoneOffsetMinutes(timezone);
      refFromDate = now.toUTC().startOf('month').minus({ month: 5 }).minus({ minutes: offset });
      refToDate = now.toUTC().endOf('month').minus({ minutes: offset });
      ref = refFromDate;
      while (ref && ref <= refToDate) {
        timePoints.push(ref);
        ref = ref.plus({ month: 1 });
      }
      break;
    case 'Last 12 Months':
      timePart = 'MONTH';
      fromDate = now.setZone(timezone).minus({ month: 11 }).startOf('month').toUTC();
      toDate = now.setZone(timezone).endOf('month').toUTC();
      offset = dateUtils.getTimezoneOffsetMinutes(timezone);
      refFromDate = now.toUTC().startOf('month').minus({ month: 11 }).minus({ minutes: offset });
      refToDate = now.toUTC().endOf('month').minus({ minutes: offset });
      ref = refFromDate;
      while (ref && ref <= refToDate) {
        timePoints.push(ref);
        ref = ref.plus({ month: 1 });
      }
      break;
    case 'Custom': {
      if (!customFromDate || !customToDate) {
        throw new Error('Custom range type requires fromDate and toDate parameters');
      }
      fromDate = DateTime.fromISO(customFromDate).toUTC();
      toDate = DateTime.fromISO(customToDate).toUTC();
      const diff = fromDate.diff(toDate, ['days']);
      if (Math.abs(diff.days) > 62) {
        timePart = 'MONTH';
      } else if (Math.abs(diff.days) > 2) {
        timePart = 'DAY';
      } else {
        const hoursDiff = fromDate.diff(toDate, ['hours']);
        if (Math.abs(hoursDiff.hours) > 50) {
          timePart = 'DAY';
        } else {
          timePart = 'HOUR';
        }
      }
      if (timePart === 'HOUR') {
        refFromDate = DateTime.fromISO(customFromDate).toUTC().startOf('hour');
        refToDate = DateTime.fromISO(customToDate).toUTC().endOf('hour');
        ref = refFromDate;
        while (ref && ref <= refToDate) {
          timePoints.push(ref);
          ref = ref.plus({ hour: 1 });
        }
      } else if (timePart === 'DAY') {
        offset = dateUtils.getTimezoneOffsetMinutes(timezone);
        refFromDate = DateTime.fromISO(customFromDate).toUTC().startOf('day').minus({ minutes: offset });
        refToDate = DateTime.fromISO(customToDate).toUTC().endOf('day').minus({ minutes: offset });
        ref = refFromDate;
        while (ref && ref <= refToDate) {
          timePoints.push(ref); // The SQL will return 00 for the hours when grouping by day
          ref = ref.plus({ day: 1 });
        }
      } else if (timePart === 'MONTH') {
        offset = dateUtils.getTimezoneOffsetMinutes(timezone);
        refFromDate = DateTime.fromISO(customFromDate).toUTC().startOf('month').minus({ minutes: offset });
        refToDate = DateTime.fromISO(customToDate).toUTC().endOf('month').minus({ minutes: offset });
        ref = refFromDate;
        while (ref && ref <= refToDate) {
          timePoints.push(ref);
          ref = ref.plus({ month: 1 });
        }
      }
      break;
    }
    default:
      throw new Error(`Unsupported Range Type: ${rangeType}`);
  }

  if (timePart === 'HOUR') {
    offset = 0;
    timeFormat = 'h a';
  } else if (timePart === 'DAY') {
    offset = dateUtils.getTimezoneOffsetMinutes(timezone);
    timeFormat = 'MM/dd';
  } else if (timePart === 'MONTH') {
    offset = dateUtils.getTimezoneOffsetMinutes(timezone);
    timeFormat = 'YYYY/MM';
  }

  const timeScaleData: TimeScaleData = {
    offset,
    fromDate: fromDate?.toISO() || null,
    toDate: toDate?.toISO() || null,
    timePoints: timePoints.map((x: DateTime) => x.toISO()),
    timePart,
    timeFormat,
  };

  return timeScaleData;
};

export { analyticsUtils };
